import * as Sentry from '@sentry/react';
import type { GraphqlNetworkFailure } from '@ths/shared/observability';
import React from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

/**
 * Initializes Sentry error tracking and performance monitoring.
 *
 * Includes:
 * - React Router v6 integration for accurate route tracking
 * - Browser tracing for performance monitoring
 * - Session replay (production only) with PII masking
 * - PII filtering in beforeSend hook
 * - Error filtering for known non-critical errors
 */
export function initializeSentry(): void {
  const sentryDsn = import.meta.env.VITE_APP_SENTRY_DSN;
  const environment = import.meta.env.VERCEL_ENV;
  const release = import.meta.env.VITE_APP_VERSION;
  const graphqlApiUrl = import.meta.env.VITE_APP_GRAPHQL_API_URL;

  // Skip initialization if DSN is not configured
  if (!sentryDsn) {
    if (environment === 'development') {
      console.warn('Sentry DSN not configured. Error tracking is disabled.');
    }
    return;
  }

  const isProduction = environment === 'production';

  Sentry.init({
    dsn: sentryDsn,
    environment,
    release: `client-portal@${release}`,
    enabled: environment !== 'development' && environment !== 'local',

    // Distributed Tracing: Send sentry-trace and baggage headers to backend
    tracePropagationTargets: graphqlApiUrl ? [graphqlApiUrl] : [],

    // Integrations
    integrations: [
      // React Router v6 integration for accurate route tracking
      Sentry.reactRouterV6BrowserTracingIntegration({
        useEffect: React.useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),

      // Session Replay - enabled in both environments
      Sentry.replayIntegration({
        // Privacy: Mask all text and media by default to protect PII
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,

        // Network capture configuration
        // Only capture detailed network info for GraphQL API.
        networkDetailAllowUrls: graphqlApiUrl ? [graphqlApiUrl] : [],
        networkCaptureBodies: false,
        networkRequestHeaders: ['X-Client-Request-ID', 'X-Client-Version'],
        networkResponseHeaders: [
          'X-Request-ID',
          'X-Response-Time',
          'X-RateLimit-Remaining',
        ],

        // Performance optimization
        mutationLimit: 10000, // Stop recording after 10k DOM mutations
        mutationBreadcrumbLimit: 750, // Warn on large mutations

        // Session configuration
        minReplayDuration: 5000, // Only send replays >= 5 seconds
        maxReplayDuration: 3600000, // Max 1 hour per replay session
        stickySession: true, // Track users across page refreshes

        // Custom masking for sensitive fields
        beforeAddRecordingEvent: (event) => {
          // Additional runtime filtering if needed
          return event;
        },
      }),
    ],

    // Performance Monitoring
    tracesSampleRate: isProduction ? 0.1 : 1.0, // 10% in prod, 100% in dev

    // Session Replay sample rates
    // In production: sample 10% of normal sessions, 100% of error sessions
    // In development: sample 100% of all sessions for testing
    replaysSessionSampleRate: isProduction ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0, // Always capture sessions with errors

    // PII Filtering
    beforeSend(event) {
      // Filter out sensitive headers
      if (event.request?.headers) {
        const headers = event.request.headers;
        delete headers.Authorization;
        delete headers.Cookie;
        delete headers['X-Auth-Token'];
      }

      // Redact tokens from query strings
      if (event.request?.url) {
        event.request.url = event.request.url.replace(
          /([?&])(token|access_token|api_key|apikey)=[^&]*/gi,
          '$1$2=REDACTED',
        );
      }

      // Redact common PII fields from event data
      if (event.contexts) {
        const piiFields = [
          'password',
          'email',
          'phone',
          'ssn',
          'creditCard',
          'token',
          'apiKey',
        ];

        const redactPII = (obj: unknown): unknown => {
          if (obj === null || typeof obj !== 'object') return obj;
          if (Array.isArray(obj)) {
            return obj.map(redactPII);
          }

          const record = obj as Record<string, unknown>;
          for (const key in record) {
            if (piiFields.some((field) => key.toLowerCase().includes(field))) {
              record[key] = 'REDACTED';
            } else if (
              typeof record[key] === 'object' &&
              record[key] !== null
            ) {
              redactPII(record[key]);
            }
          }
          return obj;
        };

        redactPII(event.contexts);
      }

      return event;
    },

    // Error Filtering - ignore known non-critical errors
    ignoreErrors: [
      // Browser extension errors
      'top.GLOBALS',
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'atomicFindClose',

      // Network errors that are expected
      'NetworkError',
      'Network request failed',
      'Failed to fetch',
      'Load failed',
      /\/sw\.js load failed/i,

      // Auth0 expected errors
      'Login required',
      'Consent required',
      'consent_required',
      'login_required',

      // ResizeObserver errors (harmless)
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
    ],

    // Ignore errors from browser extensions
    denyUrls: [
      // Chrome extensions
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      // Firefox extensions
      /^moz-extension:\/\//i,
      // Safari extensions
      /^safari-extension:\/\//i,
    ],
  });

  if (environment === 'development') {
    console.log('Sentry initialized successfully', {
      environment,
      release: `client-portal@${release}`,
      tracesSampleRate: isProduction ? 0.1 : 1.0,
    });
  }
}

export function reportGraphqlNetworkFailure(
  failure: GraphqlNetworkFailure,
): void {
  if (failure.online === false) {
    Sentry.addBreadcrumb({
      category: 'graphql-network',
      message: 'GraphQL request failed while the browser was offline',
      level: 'info',
      data: {
        operationName: failure.operationName,
        attemptCount: failure.attemptCount,
      },
    });
    return;
  }

  const error = new Error(
    `GraphQL network request failed: ${failure.operationName}`,
  );
  error.name = 'GraphqlNetworkFailure';

  Sentry.captureException(error, {
    tags: {
      app: 'client-app',
      errorType: 'graphql-network',
      operationName: failure.operationName,
      networkCategory: failure.category,
      httpStatus: failure.httpStatus ?? 'none',
      client_request_id: failure.clientRequestId,
      request_id: failure.serverRequestId ?? 'none',
    },
    contexts: { graphqlNetwork: { ...failure } },
    fingerprint: [
      'graphql-network',
      'client-app',
      failure.operationName,
      failure.category,
      String(failure.httpStatus ?? 'none'),
    ],
  });
}
